import { createFileRoute, Outlet, Navigate, useLocation } from "@tanstack/react-router";
import { useEffect } from "react";
import { SelectDomainProvider } from "@/features/free-plan";
import { useAuth } from "@/features/auth";
import Navigation from "@/features/navigation";
import { LoadingIndicator } from "@/components";
import { SubscriptionRenewalDialog, useSubscriptionRenewalDialog } from "@/features/subscriptions";
import { RouterModalsLayout } from "@/components/router-modals-layout/router-modals-layout";
import { SettingsProvider, useSettings } from "../../features/settings/contexts/settings-context";

const Component = ({ isUserLoading }: { isUserLoading: boolean }) => {
  const { settings, isFetching: isSettingsDataFetching } = useSettings();
  const { pathname, search } = useLocation();
  const isOnOnboardingPage = pathname === "/onboarding";

  const searchParams = new URLSearchParams(search);
  const hasModal = searchParams.get("modal");

  // Added to hide the default cookie popup renew button; Custom button is added in the bottom of the app sidebar
  useEffect(() => {
    const styleElement = document.createElement("style");
    if (!isOnOnboardingPage) {
      styleElement.innerHTML = `
      #Coi-Renew {
        visibility: hidden;
      }
    `;

      document.head.appendChild(styleElement);
    }

    return () => {
      styleElement.remove();
    };
  }, [isOnOnboardingPage]);

  const subscriptionRenewalDialog = useSubscriptionRenewalDialog();

  const isInitialLoading = isUserLoading || (!settings && isSettingsDataFetching);

  if (isInitialLoading) {
    return <LoadingIndicator />;
  }

  if (!settings?.settingsData) return null;

  const { onboardingStatus } = settings.settingsData;

  const isOnboardingInProgress = !["DONE", "SKIPPED", "NOT_SHOWN", null, undefined].includes(onboardingStatus);
  const shouldRedirectToOnboarding = onboardingStatus && isOnboardingInProgress;
  const showFullNavigation = !onboardingStatus || !isOnboardingInProgress;

  if (shouldRedirectToOnboarding && !isOnOnboardingPage) {
    return <Navigate to="/onboarding" replace />;
  }

  if (!shouldRedirectToOnboarding && isOnOnboardingPage && !hasModal) {
    return <Navigate to="/consent-solutions" replace />;
  }

  return (
    <SelectDomainProvider>
      <RouterModalsLayout>
        {subscriptionRenewalDialog?.showSubscriptionRenewalDialog && <SubscriptionRenewalDialog />}
        <Navigation showFullNavigation={showFullNavigation}>
          <Outlet />
        </Navigation>
      </RouterModalsLayout>
    </SelectDomainProvider>
  );
};

const AuthenticatedLayout = () => {
  const { user, isInitialized, isLoading } = useAuth();

  if (!isInitialized || isLoading) {
    return <LoadingIndicator />;
  }

  if (user === null) {
    return <Navigate to="/login" />;
  }

  return (
    <SettingsProvider>
      <Component isUserLoading={isLoading} />
    </SettingsProvider>
  );
};

const Route = createFileRoute("/_authorized")({
  component: () => <AuthenticatedLayout />,
});

export { Route };
